<template>
    <div
        v-if="medicalCourses !== null"
        class="container-fluid">
        <div class="card">
            <div class="card-header">スケジュール管理</div>
            <div class="card-body">
                <div class="conditionsWrapper d-flex align-items-center flex-wrap">
                    <div class="d-flex align-items-center mb-2 mr-3">
                        <label class="mb-0 mr-2 text-muted">{{ $locale.consultation_host }}科:</label>
                        <multiselect
                            v-model="selectedMedicalCourse"
                            :options="medicalCourses"
                            :placeholder="`${$locale.consultation_host}科を選択してください`"
                            :multiple="false"
                            :close-on-select="true"
                            :clear-on-select="true"
                            :hide-selected="true"
                            :preserve-search="true"
                            :disabled="inProgress"
                            selectLabel="選択　"
                            label="name"
                            track-by="id"
                            @input="fetchDoctorSchedules">
                        </multiselect>
                    </div>
                    <div class="d-flex align-items-center mb-2 mr-3">
                        <label class="mb-0 mr-2 text-muted">日付:</label>
                        <div class="chartStartWrapper">
                            <flat-pickr
                                v-model="chartStart"
                                :config="{ locale: flatPickerLocale }"
                                class="form-control"
                                @input="onChangeChartStart">
                            </flat-pickr>
                        </div>
                        <div>&nbsp;〜&nbsp;</div>
                        <div class="chartEndWrapper">
                            <flat-pickr
                                v-model="chartEnd"
                                :config="{ locale: flatPickerLocale }"
                                disabled
                                class="form-control">
                            </flat-pickr>
                        </div>
                    </div>
                    <div class="d-flex align-items-center mb-2 mr-3">
                        <label
                            for="display"
                            class="mb-0 mr-2 text-muted">
                            表示形式:
                        </label>
                        <div>
                            <select
                                v-model="display"
                                @change="fetchDoctorSchedules"
                                name="display"
                                id="display"
                                class="form-control custom-select">
                                <option value="doctor_menu">メニュー</option>
                                <option value="doctor">医師</option>
                            </select>
                        </div>
                    </div>
                    <div class="d-flex align-items-center mb-2">
                        <label
                            for="timezone"
                            class="mb-0 mr-2 text-muted">
                            タイムゾーン:
                        </label>
                        <div>
                            <select
                                v-model="timezone"
                                @change="fetchDoctorSchedules"
                                name="timezone"
                                id="timezone"
                                class="form-control custom-select">
                                <option v-for="timezone in timezones" :value="timezone.key">
                                    {{ timezone.value }}
                                </option>
                            </select>
                        </div>
                    </div>
                </div>

                <div
                    v-if="inProgress"
                    class="p-5 d-flex justify-content-center align-items-center">
                    <b-spinner variant="yokumiru-admin"/>
                </div>
                <div v-else-if="canShowGanttChart">
                    <div class="ganttChartWrapper">
                        <g-gantt-chart
                            :chart-start="chartStart"
                            :chart-end="chartEndForGantt"
                            :grid="true"
                            :highlighted-hours="[0]"
                            :row-label-width="display === 'doctor_menu' ? '8%' : '4%'"
                            :row-height="32"
                            :width="`${days * 600}px`">
                            <g-gantt-row
                                v-for="(schedule, index) in chartSchedules"
                                :key="`schedule_${index}`"
                                :label="schedule.label"
                                :bars="schedule.schedules"
                                :highlight-on-hover="true"
                                :class="{ 'isMatchingMenu' : schedule.isMatching, 'isMatchingMenuTimeline' : schedule.label === 'マッチング予約' }"
                                bar-start="start"
                                bar-end="end">
                            </g-gantt-row>
                        </g-gantt-chart>
                    </div>
                    <div class="axisInfoWrapper d-flex align-items-center">
                        <div class="axisInfo axisInfoAvailable small text-muted">予約可能</div>
                        <div class="axisInfo axisInfoReserved small text-muted">予約済</div>
                        <div
                            v-if="display === 'doctor_menu'"
                            class="axisInfo axisInfoReservedAlt small text-muted">
                            予約済（他メニュー）
                        </div>
                        <div
                            v-if="display === 'doctor' && $store.state.configs.reservation_multiple == 1"
                            class="axisInfo axisInfoReservedAlt small text-muted">
                            予約済（空き枠あり）
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import constants from '../../constants'
import moment from 'moment-timezone'
import YokumiruHelper from '../../YokumiruHelper'
import Multiselect from 'vue-multiselect'
import flatPickr from 'vue-flatpickr-component'
import { Japanese } from 'flatpickr/dist/l10n/ja.js'
import 'flatpickr/dist/flatpickr.css'
import { GGanttChart, GGanttRow } from 'vue-ganttastic'

export default {
    name: 'AdminDoctorSchedules',

    components: {
        Multiselect,
        flatPickr,
        GGanttChart,
        GGanttRow,
    },

    data: function () {
        return {
            helper: YokumiruHelper,
            flatPickerLocale: Japanese,
            inProgress: false,
            medicalCourses: null,
            timezones: null,
            selectedMedicalCourse: null,
            chartStart: null,
            chartEnd: null,
            display: 'doctor_menu',
            timezone: 'Asia/Tokyo',
            doctorSchedules: null,
            doctorIsMatchingSchedules: null,
            days: 6,
            timer: null,
        }
    },

    computed: {
        /**
         * スケジュール取得可能判定
         *
         * @return {boolean}
         */
        canFetchSchedules: function () {
            return this.selectedMedicalCourse !== null
                && this.chartStart !== null
                && this.chartEnd !== null
                && this.endpointData !== null
        },

        /**
         * スケジュール表の表示可能判定
         *
         * @return {boolean}
         */
        canShowGanttChart: function () {
            return this.canFetchSchedules
                && this.doctorSchedules !== null
        },

        /**
         * スケジュール表用に整形されたデータを返す
         *
         * @returns {array}
         */
        chartSchedules: function () {
            if (this.doctorSchedules === null) return []

            const result = []

            // マッチング予約のガントチャート
            if (this.doctorIsMatchingSchedules !== null) {
                const tmpSchedules = { label: 'マッチング予約', schedules: [] }

                for (let [doctorMenuId, items] of Object.entries(this.doctorIsMatchingSchedules)) {
                    for (let [date, value] of Object.entries(items)) {
                        for (const schedule of value.schedules) {
                            const rCount = this.calcIsMatchingDoctorReservedCount(date, schedule.start_time)

                            let bgColor = '#F0C72F' // 予約可能

                            if (rCount.reservedDoctorCount >= rCount.doctorCount) {
                                bgColor = '#EE7321' // 予約済
                            }
                            else if (rCount.reservedDoctorCount > 0) {
                                bgColor = '#bbbbbb' // 予約済（空き枠あり）
                            }

                            // @see https://github.com/sewii/Yokumiru/issues/794
                            const endDate = schedule.is_split ? moment(`${date} 00:00:00`).add(1, 'days').format('YYYY-MM-DD') : date

                            tmpSchedules.schedules.push({
                                start: date + ' ' + schedule.start_time,
                                end: endDate + ' ' + schedule.end_time,
                                ganttBarConfig: {
                                    color: '#212529',
                                    backgroundColor: bgColor,
                                    borderRadius: 5,
                                },
                            })
                        }
                    }
                }
                result.push(tmpSchedules)
            }

            // メニュー毎 or 医師毎のガントチャート
            for (let [doctorMenuId, items] of Object.entries(this.doctorSchedules)) {
                const tmpSchedules = { label: null, schedules: [], isMatching: false }

                for (let [date, value] of Object.entries(items)) {
                    if (tmpSchedules.label === null) {
                        if (this.display === 'doctor_menu') {
                            tmpSchedules.label = `${value.doctor.full_name}／${value.doctor_menu.title}（${value.doctor_menu.consultation_minutes}分）`
                        }
                        else {
                            tmpSchedules.label = value.doctor.full_name
                        }
                    }

                    if (!tmpSchedules.isMatching) {
                        tmpSchedules.isMatching = value.doctor_menu.consultation_minutes === this.selectedMedicalCourse.matching_minutes
                            && value.doctor_menu.consultation_price === this.selectedMedicalCourse.matching_price
                    }

                    for (const schedule of value.schedules) {
                        const isFullReserved = schedule.is_full_reserved
                        const isInclude = schedule.reserved_doctor_menu_ids.includes(Number(doctorMenuId))

                        let bgColor = '#F0C72F' // 予約可能

                        // 予約の空き状況に応じて色変更
                        // @see App\Http\Controllers\Admin\Api\DoctorSchedulesController::makeIntervalArray()
                        if (this.display === 'doctor_menu') {
                            if (isInclude) bgColor = '#EE7321' // 予約済
                            else if (isFullReserved) bgColor = '#bbbbbb' // 予約済（他メニュー）
                        }
                        else if (schedule.reserved_doctor_menu_ids.length > 0) {
                            if (isFullReserved) bgColor = '#EE7321'
                            else bgColor = '#bbbbbb' // 予約済（空き枠あり）
                        }

                        // @see https://github.com/sewii/Yokumiru/issues/794
                        const endDate = schedule.is_split ? moment(`${date} 00:00:00`).add(1, 'days').format('YYYY-MM-DD') : date

                        tmpSchedules.schedules.push({
                            start: date + ' ' + schedule.start_time,
                            end: endDate + ' ' + schedule.end_time,
                            ganttBarConfig: {
                                color: '#212529',
                                backgroundColor: bgColor,
                                borderRadius: 5,
                            },
                        })
                    }
                }
                result.push(tmpSchedules)
            }

            return result
        },

        /**
         * vue-ganttastic用の終了日付範囲（※指定された日付の前日まで表示される仕様のため）
         *
         * @returns {string|null}
         */
        chartEndForGantt: function () {
            if (!this.chartEnd) {
                return null
            }
            return moment(this.chartEnd, 'YYYY-MM-DD').add(1, 'days').format('YYYY-MM-DD')
        },

        /**
         * スケジュール取得APIのエンドポイントをクエリパラメータ付きで返却
         *
         * @return {null|object}
         */
        endpointData: function () {
            if (this.selectedMedicalCourse === null) {
                return null
            }

            const endpoint = constants.admin.doctor_schedules.endpoint.base
            const start = moment(`${this.chartStart} 00:00:00`)
            const end = moment(`${this.chartStart} 23:59:59`).add(this.days, 'days')

            // 指定されたタイムゾーンとJST間の時差を考慮して日付範囲を±1広げる
            const extendedStart = start.clone().subtract(1, 'days')
            const extendedEnd = end.clone().add(1, 'days')

            // スケジュール表の日付範囲を保持
            const rStart = start.clone()
            const rEnd = end.clone()
            const rangeDates = []
            while (rStart.diff(rEnd) <= 0) {
                rangeDates.push(rStart.date())
                rStart.add(1, 'days')
            }

            let query = `?medical_course_id=${this.selectedMedicalCourse.id}`
            query += `&start_date=${extendedStart.format('YYYY-MM-DD')}`
            query += `&end_date=${extendedEnd.format('YYYY-MM-DD')}`

            return {
                endpoint: `${endpoint}${query}`,
                extendedStart: extendedStart,
                extendedEnd: extendedEnd,
                rangeDates: rangeDates,
            }
        },
    },

    watch: {
        /**
         * スケジュール表の日付フォーマットを変更
         * （vue-ganttasticがフォーマット変更非対応のため）
         *
         * @param {Array} val
         */
        chartSchedules: function (val) {
            if (val && val.length > 0) {
                clearInterval(this.timer)

                this.timer = setInterval(() => {
                    const date = moment(this.chartStart, 'YYYY-MM-DD')
                    const timeAxis = window.document.getElementById('g-timeaxis')

                    if (timeAxis) {
                        const timeAxisDays = window.document.querySelectorAll('#g-timeaxis > div.g-timeaxis-days > div.g-timeaxis-day > div:first-child')
                        timeAxisDays.forEach((timeAxisDay) => {
                            timeAxisDay.innerText = date.format('YYYY年M月D日 (ddd)')
                            date.add(1, 'days')
                        })
                        clearInterval(this.timer)

                        // vue-ganttastic ライブラリのバグ対応
                        // 何故か最初の日付だけタイムライン表示されないためjsでDOM追加する
                        const timeAxisTimes = window.document.querySelectorAll('#g-timeaxis > div.g-timeaxis-days > div.g-timeaxis-day > div:last-child')
                        timeAxisTimes[0].innerHTML = timeAxisTimes[1].innerHTML
                    }
                }, 100)
            }
        },

        /**
         * 日付範囲の変更時
         */
        chartStart: function () {
            if (this.endpointData === null) return

            axios.get(`${this.endpointData.endpoint}&display=${this.display}&is_matching=1`)
                .then(({data}) => {
                    this.doctorIsMatchingSchedules = this.convertSchedulesFromJST(
                        data.data,
                        this.endpointData.extendedStart,
                        this.endpointData.extendedEnd,
                        this.endpointData.rangeDates,
                        this.timezone
                    )
                })
        },

        /**
         * 相談科の選択変更時
         */
        selectedMedicalCourse: function () {
            if (this.endpointData === null) return

            axios.get(`${this.endpointData.endpoint}&display=${this.display}&is_matching=1`)
                .then(({data}) => {
                    this.doctorIsMatchingSchedules = this.convertSchedulesFromJST(
                        data.data,
                        this.endpointData.extendedStart,
                        this.endpointData.extendedEnd,
                        this.endpointData.rangeDates,
                        this.timezone
                    )
                })
        },

        /**
         * タイムゾーンの選択変更時
         */
        timezone: function () {
            if (this.endpointData === null) return

            axios.get(`${this.endpointData.endpoint}&display=${this.display}&is_matching=1`)
                .then(({data}) => {
                    this.doctorIsMatchingSchedules = this.convertSchedulesFromJST(
                        data.data,
                        this.endpointData.extendedStart,
                        this.endpointData.extendedEnd,
                        this.endpointData.rangeDates,
                        this.timezone
                    )
                })
        },
    },

    created: function () {
        this.chartStart = moment().format('YYYY-MM-DD')
        this.chartEnd = moment().add(this.days, 'days').format('YYYY-MM-DD')
        this.fetchTimezones()
        this.fetchMedicalCourses()
    },

    mounted: function () {
        setInterval(() => {
            const tooltip = document.querySelector('div.g-gantt-tooltip')
            if (tooltip && !tooltip.classList.contains('moved')) {
                const left = Number(tooltip.style.left.replace('px', ''))
                tooltip.style.left = left - 11 + 'px'
                tooltip.classList.add('moved')
            }
        }, 100)
    },

    methods: {
        /**
         * 指定された日時における予約枠総数と予約数を取得
         * @param {string} date
         * @param {string} start_time
         *
         * @return {object}
         */
        calcIsMatchingDoctorReservedCount: function (date, start_time) {
            const doctorIds = []
            const reservedDoctorIds = []

            for (let [doctorMenuId, items] of Object.entries(this.doctorIsMatchingSchedules)) {
                for (const schedule of items[date].schedules) {
                    if (schedule.start_time === start_time) {
                        const doctorId = items[date].doctor_menu.doctor_id

                        if (doctorIds.every((id) => id !== doctorId)) {
                            doctorIds.push(doctorId)
                        }

                        if (schedule.reserved_doctor_menu_ids.length > 0) {
                            if (reservedDoctorIds.every((id) => id !== doctorId)) {
                                reservedDoctorIds.push(doctorId)
                            }
                        }
                    }
                }
            }

            // デバッグ用
            // if (date === '2022-11-16' && start_time === '07:00') {
            //     console.log(doctorIds, reservedDoctorIds, doctorIds.length, reservedDoctorIds.length)
            // }

            return {
                doctorCount: doctorIds.length,
                reservedDoctorCount: reservedDoctorIds.length,
            }
        },

        /**
         * 日付変更時
         */
        onChangeChartStart: function () {
            this.chartEnd = moment(this.chartStart, 'YYYY-MM-DD').add(this.days, 'days').format('YYYY-MM-DD')
            this.fetchDoctorSchedules()
        },

        /**
         * タイムゾーン一覧取得
         */
        fetchTimezones: function () {
            return axios.get(constants.admin.common.endpoint.timezone)
                .then(({data}) => {
                    const momentTimezones = moment.tz.names()
                    this.timezones = data.data.filter((d) => momentTimezones.includes(d.key))
                })
        },

        /**
         * 相談科一覧取得
         */
        fetchMedicalCourses: function () {
            return axios.get(constants.admin.medical_courses.endpoint.base)
                .then(({data}) => {
                    this.medicalCourses = data
                })
        },

        /**
         * 医師スケジュール取得
         */
        fetchDoctorSchedules: async function () {
            if (this.inProgress || !this.canFetchSchedules) return
            this.inProgress = true

            await axios.get(`${this.endpointData.endpoint}&display=${this.display}&is_matching=0`)
                .then(({data}) => {
                    this.doctorSchedules = this.convertSchedulesFromJST(
                        data.data,
                        this.endpointData.extendedStart,
                        this.endpointData.extendedEnd,
                        this.endpointData.rangeDates,
                        this.timezone
                    )
                })

            this.inProgress = false
        },

        /**
         * スケジュールの時差変換処理
         *
         * @params {object} data
         * @params {moment} start
         * @params {moment} end
         * @params {array<Number>} rangeDates
         * @params {string} timezone
         *
         * @return {object}
         */
        convertSchedulesFromJST: function (data, start, end, rangeDates, timezone) {
            const convertedData = {}

            for (let [doctorMenuId, items] of Object.entries(data)) {
                const holder = {}
                const loopStart = start.clone()

                while (loopStart.diff(end) <= 0) {
                    const dateFormat = loopStart.format('YYYY-MM-DD')

                    if (items[dateFormat] && items[dateFormat].count > 0) {
                        items[dateFormat].schedules.forEach((schedule) => {
                            const st = this.helper.userTime(`${dateFormat} ${schedule.start_time}`, timezone)
                            const edDateFormat = schedule.is_split ? loopStart.clone().add(1, 'days').format('YYYY-MM-DD') : dateFormat
                            const ed = this.helper.userTime(`${edDateFormat} ${schedule.end_time}`, timezone)

                            // スケジュール表の日付範囲内のみ処理
                            if (rangeDates.includes(st.date())) {
                                schedule.start_time = st.format('HH:mm')
                                schedule.end_time = ed.format('HH:mm')

                                // 開始時間が終了時間より小さい場合のみ処理（サマータイム対策）
                                if (st.diff(ed) < 0 && !this.hasHolder(holder, st, schedule)) {
                                    this.prepareHolder(holder, loopStart, items[dateFormat])
                                    this.prepareHolder(holder, st, items[dateFormat])
                                    this.pushToHolder(holder, st, schedule)
                                }
                            }
                        })
                    }
                    else {
                        // スケジュール表の日付範囲内のみ処理
                        if (rangeDates.includes(loopStart.date())) {
                            this.prepareHolder(holder, loopStart, items[dateFormat])
                        }
                    }
                    loopStart.add(1, 'days')
                }
                convertedData[doctorMenuId] = holder
            }

            return convertedData
        },

        hasHolder: function (holder, date, schedule) {
            if (!holder[date.format('YYYY-MM-DD')]) {
                return false
            }

            const result = holder[date.format('YYYY-MM-DD')].schedules.find((s) => {
                return schedule.start_time === s.start_time && schedule.end_time === s.end_time
            })

            return !!result
        },

        prepareHolder: function (holder, date, item) {
            if (!holder[date.format('YYYY-MM-DD')]) {
                holder[date.format('YYYY-MM-DD')] = {
                    schedules: [],
                    count: 0,
                    day: date.day(),
                    doctor: item['doctor'],
                    doctor_menu: item['doctor_menu'],
                }
            }
        },

        pushToHolder: function (holder, date, schedule) {
            // end が 00:00 フォーマットだと表示が崩れるので 24:00 にする
            if (schedule.end_time === '00:00') {
                schedule.end_time = '24:00'
            }
            holder[date.format('YYYY-MM-DD')].schedules.push(schedule)
            holder[date.format('YYYY-MM-DD')].count += 1
        },
    },
}
</script>

<style lang="scss" scoped>
.conditionsWrapper {
    margin-bottom: 20px;
}

.ganttChartWrapper {
    width: 100%;
    height: 100%;
    overflow-x: scroll;
}

.axisInfoWrapper {
    margin-top: 15px;

    .axisInfo {
        margin-right: 15px;
        line-height: 10px;

        &:before {
            position: relative;
            top: 1px;
            content: '';
            display: inline-block;
            width: 10px;
            height: 10px;
            line-height: 10px;
            margin-right: 3px;
        }

        &.axisInfoAvailable {
            &::before {
                background-color: #F0C72F;
            }
        }

        &.axisInfoReserved {
            &::before {
                background-color: #EE7321;
            }
        }

        &.axisInfoReservedAlt {
            &::before {
                background-color: #bbbbbb;
            }
        }
    }
}
</style>

<style lang="scss">
/**
 * vue-multiselect
 */
@import '~vue-multiselect/dist/vue-multiselect.min.css';

.multiselect {
    width: 310px;
    height: 38px;
    min-height: 38px;
}

.multiselect__tags {
    height: 38px;
    min-height: 38px;
    padding-top: 6px;
}

.multiselect__input,
.multiselect__single {
    font-size: 14px;
    line-height: 26px;
}

/**
 * flatpickr
 */
.chartStartWrapper {
    .flatpickr-input[readonly] {
        &.form-control[readonly] {
            background-color: #fff;
        }
    }
}

.chartEndWrapper {
    .flatpickr-input[readonly] {
        &.form-control[readonly] {
            cursor: default;
        }
    }
}

/**
 * vue-ganttastic
 */
#g-timeaxis-marker {
    visibility: hidden;
}

.g-gantt-row {
    .g-gantt-row-label {
        display: block !important;
        font-size: 12px !important;
        line-height: 32px;
        padding-left: 12px;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        border-top: 1px solid #fff;
        border-bottom: 1px solid #fff;
    }

    &.isMatchingMenu {
        .g-gantt-row-label {
            position: relative;

            &::after {
                content: '';
                position: absolute;
                right: 0;
                top: 0;
                border-top: 10px solid #F77696;
                border-bottom: 10px solid transparent;
                border-left: 10px solid transparent;
            }
        }
    }

    &.isMatchingMenuTimeline {
        .g-gantt-row-label {
            color: #fff !important;
            background-color: #F77696 !important;
        }

        .g-gantt-row-bars-container {
            background-color: rgba(247, 118, 150, 0.15) !important;
        }
    }
}
</style>
